import { create } from 'zustand'
import { v4 as uuid } from 'uuid'
import {
  listMessages,
  type ConversationMeta,
  type ListMessagesParams,
  type Message,
  type MessagesResponse,
} from '../api/messages'

declare global {
  interface Window {
    __RPA_DEBUG?: {
      store?: Record<string, unknown>
      [key: string]: unknown
    }
  }
}

export interface MessageFilters {
  unreadOnly: boolean
  flaggedOnly: boolean
  onAirOnly: boolean
  platforms: string[]
  intents: string[]
  q: string
}

export type IncomingMessage = Message & {
  is_read: boolean | number
  is_flagged: boolean | number
  is_on_air: boolean | number
  is_autoresponder: boolean | number
  auto_rule_id?: number | string | null
  auto_rule_name?: string | null
}

export interface FetchMessagesOptions {
  cursor?: number | null
  dir?: 'older' | 'newer'
  limit?: number
  append?: boolean
  contactId?: number | null
  identityId?: number | null
  includeBlocked?: boolean
  ignoreFilters?: boolean
}

export type ThreadMessages = {
  contactId: number
  items: Message[]
  firstUnreadId: number | null
  loading: boolean
  requestId?: string
  lastLoadedAt?: number
  meta?: ConversationMeta | null
  error?: string | null
}

type MessagesByContact = Record<number, ThreadMessages>

interface MessagesState {
  filters: MessageFilters
  items: Message[]
  nextCursor: number | null
  prevCursor: number | null
  loading: boolean
  unreadCount: number
  connected: boolean
  lastId: number
  lastUpdateId: number
  lastIncoming: Message | null
  defaultPlatforms: string[]
  byContact: MessagesByContact
  activeContactId: number | null
  activeStationId: number | null
  jumpTarget: { contactId: number; messageId: number; highlight?: boolean; ts: number } | null
  applyFilters: (updates: Partial<MessageFilters>) => void
  setFilter: <K extends keyof MessageFilters>(key: K, value: MessageFilters[K]) => void
  fetchPage: (stationId: number, options?: FetchMessagesOptions) => Promise<MessagesResponse>
  appendBatch: (messages: IncomingMessage[]) => void
  updateMessage: (id: number, updates: Partial<Message>) => void
  markReadUpTo: (contactId: number, upToId: number) => void
  markConversationReadUpTo: (contactId: number, upToId: number) => void
  markReadSingle: (contactId: number, messageId: number) => void
  setConnected: (connected: boolean) => void
  setUnreadCount: (count: number) => void
  setLastUpdateId: (id: number) => void
  consumeIncoming: () => Message | null
  setDefaultPlatforms: (platforms: string[]) => void
  setActiveContact: (
    contactId: number | null,
    stationId: number | null,
    options?: { force?: boolean },
  ) => void
  setJumpTarget: (contactId: number, messageId: number, options?: { highlight?: boolean }) => void
  consumeJumpTarget: (contactId: number, messageId: number) => void
  loadThread: (contactId: number, stationId: number, options?: { force?: boolean }) => Promise<void>
  upsertThreadMessage: (contactId: number, message: Message) => void
  removeThreadMessage: (contactId: number, id: number) => void
  updateThreadMeta: (
    contactId: number,
    updater: (prev: ConversationMeta | null) => ConversationMeta | null,
  ) => void
  addOptimisticOutbound: (
    contactId: number,
    text: string,
    identityId?: number | null,
    options?: {
      stationId?: number | null
      replyToMessageId?: number | null
      sentByUserId?: number | null
      sentByDisplayName?: string | null
      platform?: string | null
      contactName?: string | null
      contactLocation?: string | null
    },
  ) => number
  finalizeOptimisticOutbound: (contactId: number, tempId: number, message: Message) => Message | null
  failOptimisticOutbound: (contactId: number, tempId: number, error: string) => void
  resetFilters: () => void
  reset: () => void
}

const DEFAULT_PLATFORMS = ['email', 'telegram']

const dedupePlatforms = (platforms: string[] = []): string[] => {
  const cleaned = platforms
    .map((value) => value.trim())
    .filter((value) => value.length > 0)
  return Array.from(new Set(cleaned))
}

const resolveDefaultPlatforms = (platforms: string[] = DEFAULT_PLATFORMS): string[] => {
  const deduped = dedupePlatforms(platforms)
  return deduped.length ? deduped : [...DEFAULT_PLATFORMS]
}

const createInitialFilters = (platforms: string[] = DEFAULT_PLATFORMS): MessageFilters => ({
  unreadOnly: false,
  flaggedOnly: false,
  onAirOnly: false,
  platforms: resolveDefaultPlatforms(platforms),
  intents: [],
  q: '',
})

const initialFilters: MessageFilters = createInitialFilters()

const toBoolean = (value: unknown): boolean => {
  if (typeof value === 'boolean') {
    return value
  }

  if (typeof value === 'number') {
    return value === 1
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'on'
  }

  return false
}

const toDirection = (value: unknown): Message['direction'] => {
  if (value === 'out' || value === 'in') {
    return value
  }

  if (value === 1 || value === '1') {
    return 'out'
  }

  return 'in'
}

const toTransportStatus = (value: unknown): Message['transport_status'] => {
  if (typeof value !== 'string') {
    return null
  }

  const normalized = value.trim().toLowerCase()

  if (normalized === 'queued' || normalized === 'sent' || normalized === 'failed') {
    return normalized
  }

  return null
}

const toNullableNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? value : null
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) {
      return null
    }

    const parsed = Number.parseInt(trimmed, 10)
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed
    }
  }

  return null
}

const toEpochSeconds = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? Math.trunc(value) : null
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) {
      return null
    }

    const parsed = Number.parseInt(trimmed, 10)
    if (Number.isFinite(parsed)) {
      return parsed
    }
  }

  return null
}

const normalizeMessage = (message: IncomingMessage): Message => ({
  ...message,
  content: message.content ?? null,
  intent: message.intent ?? null,
  round_id: message.round_id ?? null,
  reply_text: message.reply_text ?? null,
  reply_at: message.reply_at ?? null,
  reply_at_iso_utc:
    typeof message.reply_at_iso_utc === 'string' && message.reply_at_iso_utc.trim().length > 0
      ? message.reply_at_iso_utc
      : null,
  reply_at_epoch: toEpochSeconds(message.reply_at_epoch),
  reply_to_message_id: message.reply_to_message_id ?? null,
  sent_by_user_id: message.sent_by_user_id ?? null,
  direction: toDirection(message.direction),
  transport_status: toTransportStatus(message.transport_status),
  transport_error: message.transport_error ?? null,
  sent_by: message.sent_by ?? null,
  is_read: toBoolean(message.is_read),
  is_flagged: toBoolean(message.is_flagged),
  is_on_air: toBoolean(message.is_on_air),
  is_autoresponder: toBoolean(message.is_autoresponder),
  auto_rule_id: toNullableNumber(message.auto_rule_id),
  auto_rule_name:
    typeof message.auto_rule_name === 'string' && message.auto_rule_name.trim().length > 0
      ? message.auto_rule_name.trim()
      : null,
  received_at_iso_utc:
    typeof message.received_at_iso_utc === 'string' && message.received_at_iso_utc.trim().length > 0
      ? message.received_at_iso_utc
      : null,
  received_at_epoch: toEpochSeconds(message.received_at_epoch),
})

const mergeMessages = (existing: Message[], incoming: Message[]): Message[] => {
  const map = new Map<number, Message>()

  existing.forEach((item) => {
    map.set(item.id, item)
  })

  incoming.forEach((item) => {
    map.set(item.id, item)
  })

  return Array.from(map.values()).sort((a, b) => b.id - a.id)
}

const sortMessagesAscending = (items: Message[]): Message[] => {
  return [...items].sort((a, b) => a.id - b.id)
}

const matchesFilters = (message: Message, filters: MessageFilters): boolean => {
  if (filters.unreadOnly && message.is_read) {
    return false
  }

  if (filters.flaggedOnly && !message.is_flagged) {
    return false
  }

  if (filters.onAirOnly && !message.is_on_air) {
    return false
  }

  if (filters.platforms.length && !filters.platforms.includes(message.platform)) {
    return false
  }

  if (filters.intents.length) {
    const intent = message.intent ?? 'unknown'
    if (!filters.intents.includes(intent)) {
      return false
    }
  }

  if (filters.q.trim()) {
    const term = filters.q.trim().toLowerCase()
    const content = message.content ?? ''
    const contactName = message.contact?.name ?? ''
    const identityName = message.identity?.display_name ?? ''
    const matchesSearch = [content, contactName, identityName].some((value) =>
      value.toLowerCase().includes(term),
    )

    if (!matchesSearch) {
      return false
    }
  }

  return true
}

const buildListParams = (
  stationId: number,
  filters: MessageFilters,
  options: FetchMessagesOptions = {},
): ListMessagesParams => {
  const applyFilters = !options.ignoreFilters

  return {
    stationId,
    cursor: options.cursor ?? undefined,
    dir: options.dir ?? undefined,
    limit: options.limit ?? undefined,
    unreadOnly: applyFilters ? filters.unreadOnly : undefined,
    flaggedOnly: applyFilters ? filters.flaggedOnly : undefined,
    onAirOnly: applyFilters ? filters.onAirOnly : undefined,
    platforms: applyFilters ? filters.platforms : undefined,
    intents: applyFilters ? filters.intents : undefined,
    contactId: options.contactId ?? undefined,
    identityId: options.identityId ?? undefined,
    includeBlocked: options.includeBlocked,
  }
}

const THREAD_STALE_MS = 60_000
const threadControllers = new Map<number, AbortController>()

const useMessagesStore = create<MessagesState>((set, get) => ({
  filters: { ...initialFilters },
  items: [],
  nextCursor: null,
  prevCursor: null,
  loading: false,
  unreadCount: 0,
  connected: false,
  lastId: 0,
  lastUpdateId: 0,
  lastIncoming: null,
  defaultPlatforms: resolveDefaultPlatforms(),
  byContact: {},
  activeContactId: null,
  activeStationId: null,
  jumpTarget: null,
  applyFilters: (updates) => {
    set((state) => {
      let next = { ...state.filters }

      if ('unreadOnly' in updates && typeof updates.unreadOnly === 'boolean') {
        next = { ...next, unreadOnly: updates.unreadOnly }
      }

      if ('flaggedOnly' in updates && typeof updates.flaggedOnly === 'boolean') {
        next = { ...next, flaggedOnly: updates.flaggedOnly }
      }

      if ('onAirOnly' in updates && typeof updates.onAirOnly === 'boolean') {
        next = { ...next, onAirOnly: updates.onAirOnly }
      }

      if ('platforms' in updates && updates.platforms) {
        next = { ...next, platforms: dedupePlatforms(updates.platforms) }
      }

      if ('intents' in updates && updates.intents) {
        next = { ...next, intents: Array.from(new Set(updates.intents)) }
      }

      if ('q' in updates) {
        const value = updates.q
        next = { ...next, q: typeof value === 'string' ? value.trim() : '' }
      }

      return { filters: next }
    })
  },
  setFilter: (key, value) => {
    set((state) => {
      let next: MessageFilters

      switch (key) {
        case 'platforms': {
          next = { ...state.filters, platforms: dedupePlatforms(value as string[]) }
          break
        }
        case 'intents': {
          next = { ...state.filters, intents: Array.from(new Set(value as string[])) }
          break
        }
        case 'q': {
          const normalized = typeof value === 'string' ? value.trim() : ''
          next = { ...state.filters, q: normalized }
          break
        }
        default: {
          next = { ...state.filters, [key]: value } as MessageFilters
        }
      }

      return { filters: next }
    })
  },
  fetchPage: async (stationId, options = {}) => {
    const filters = get().filters

    set({ loading: true })

    try {
      const response = await listMessages(buildListParams(stationId, filters, options))
      const normalized = response.items.map((item) => normalizeMessage(item))

      set((state) => {
        let items: Message[]

        if (options.append) {
          items = mergeMessages(state.items, normalized)
        } else {
          items = normalized.sort((a, b) => b.id - a.id)
        }

        const lastId = items.reduce((max, item) => (item.id > max ? item.id : max), 0)

        return {
          items,
          nextCursor: response.next_cursor,
          prevCursor: response.prev_cursor,
          unreadCount: response.total_unread,
          lastId,
          loading: false,
        }
      })

      return response
    } catch (error) {
      set({ loading: false })
      throw error
    }
  },
  appendBatch: (incoming) => {
    if (!incoming.length) {
      return
    }

    set((state) => {
      const normalized = incoming.map((item) => normalizeMessage(item))
      const byId = new Map<number, Message>()

      state.items.forEach((item) => {
        byId.set(item.id, item)
      })

      const contactMaps = new Map<
        number,
        { thread: ThreadMessages; map: Map<number, Message> }
      >()

      let unreadDelta = 0
      let newest: Message | null = null
      let lastId = state.lastId

      normalized.forEach((item) => {
        const existing = byId.get(item.id)

        if (!existing) {
          if (!item.is_read) {
            unreadDelta += 1
          }
          if (!newest || item.id > newest.id) {
            newest = item
          }
          byId.set(item.id, item)
        } else {
          if (existing.is_read !== item.is_read) {
            unreadDelta += item.is_read ? -1 : 1
          }
          byId.set(item.id, { ...existing, ...item })
        }

        if (item.id > lastId) {
          lastId = item.id
        }

        let entry = contactMaps.get(item.contact_id)
        if (!entry) {
          const thread =
            state.byContact[item.contact_id] ??
            ({
              contactId: item.contact_id,
              items: [],
              firstUnreadId: null,
              loading: false,
              meta: null,
              error: null,
            } as ThreadMessages)

          entry = {
            thread,
            map: new Map<number, Message>(thread.items.map((message) => [message.id, message])),
          }
          contactMaps.set(item.contact_id, entry)
        }

        const existingThreadMessage = entry.map.get(item.id)
        if (existingThreadMessage) {
          entry.map.set(item.id, { ...existingThreadMessage, ...item })
        } else {
          entry.map.set(item.id, item)
        }
      })

      const items = Array.from(byId.values()).sort((a, b) => b.id - a.id)
      const nextByContact: MessagesByContact = { ...state.byContact }

      contactMaps.forEach(({ thread, map }, contactId) => {
        const ordered = sortMessagesAscending(Array.from(map.values()))
        const firstUnreadId = ordered.find((message) => !message.is_read)?.id ?? null

        nextByContact[contactId] = {
          ...thread,
          contactId,
          items: ordered,
          firstUnreadId,
          loading: thread.loading,
          requestId: thread.requestId,
          lastLoadedAt: thread.lastLoadedAt ?? Date.now(),
          meta: thread.meta ?? null,
          error: null,
        }
      })

      return {
        items,
        lastId,
        lastIncoming: newest ?? state.lastIncoming,
        unreadCount: Math.max(0, state.unreadCount + unreadDelta),
        byContact: nextByContact,
      }
    })
  },
  updateMessage: (id, updates) => {
    set((state) => {
      let unreadDelta = 0
      let changedGlobal = false

      const items = state.items.map((item) => {
        if (item.id !== id) {
          return item
        }

        changedGlobal = true
        const next = { ...item, ...updates }

        if (item.is_read !== next.is_read) {
          unreadDelta += next.is_read ? -1 : 1
        }

        return next
      })

      let contactId: number | null = null
      const globalMatch = state.items.find((item) => item.id === id)
      if (globalMatch) {
        contactId = globalMatch.contact_id
      } else {
        for (const thread of Object.values(state.byContact)) {
          const found = thread.items.find((message) => message.id === id)
          if (found) {
            contactId = thread.contactId
            break
          }
        }
      }

      let nextByContact: MessagesByContact = state.byContact
      let updatedThread = false

      if (contactId) {
        const thread = state.byContact[contactId]
        if (thread) {
          let threadChanged = false
          const threadItems = thread.items.map((message) => {
            if (message.id !== id) {
              return message
            }

            threadChanged = true
            const next = { ...message, ...updates }

            if (!changedGlobal && message.is_read !== next.is_read) {
              unreadDelta += next.is_read ? -1 : 1
            }

            return next
          })

          if (threadChanged) {
            updatedThread = true
            const firstUnreadId = threadItems.find((message) => !message.is_read)?.id ?? null
            nextByContact = {
              ...state.byContact,
              [contactId]: {
                ...thread,
                items: threadItems,
                firstUnreadId,
              },
            }
          }
        }
      }

      if (!changedGlobal && !updatedThread) {
        return state
      }

      const unreadCount = Math.max(0, state.unreadCount + unreadDelta)

      return {
        items,
        unreadCount,
        byContact: nextByContact,
      }
    })
  },
  loadThread: async (contactId, stationId, options = {}) => {
    const requestId = uuid()
    const startedAt = Date.now()
    const state = get()
    const previousThread = state.byContact[contactId]

    const existingController = threadControllers.get(contactId)
    if (existingController) {
      existingController.abort()
      if (typeof window !== 'undefined' && window.__RPA_DEBUG?.net) {
        window.console.log('[messages] loadThread abort previous', {
          contactId,
          previousRequestId: previousThread?.requestId,
        })
      }
    }

    const controller = new AbortController()
    threadControllers.set(contactId, controller)

    if (typeof window !== 'undefined' && window.__RPA_DEBUG?.net) {
      window.console.log('[messages] loadThread start', {
        contactId,
        stationId,
        requestId,
        force: options.force ?? false,
      })
    }

    set((current) => {
      const prev =
        current.byContact[contactId] ??
        ({
          contactId,
          items: [],
          firstUnreadId: null,
          loading: false,
          meta: null,
          error: null,
        } as ThreadMessages)

      return {
        byContact: {
          ...current.byContact,
          [contactId]: {
            ...prev,
            contactId,
            loading: true,
            requestId,
            error: null,
          },
        },
      }
    })

    try {
      const response = await listMessages(
        {
          stationId,
          contactId,
          firstUnread: true,
        },
        controller.signal,
      )

      if (controller.signal.aborted) {
        if (typeof window !== 'undefined' && window.__RPA_DEBUG?.net) {
          window.console.log('[messages] loadThread aborted', {
            contactId,
            stationId,
            requestId,
          })
        }
        return
      }

      const stillLatest = get().byContact[contactId]?.requestId === requestId
      if (!stillLatest) {
        if (typeof window !== 'undefined' && window.__RPA_DEBUG?.net) {
          window.console.log('[messages] loadThread stale', {
            contactId,
            stationId,
            requestId,
          })
        }
        return
      }

      const normalized = sortMessagesAscending(
        (response.items ?? []).map((item) => normalizeMessage(item as IncomingMessage)),
      )

      const hasItems = normalized.length > 0
      const forced = options.force ?? false
      const willPreserve = !forced && !hasItems && Boolean(previousThread?.items.length)

      set((current) => {
        const prev =
          current.byContact[contactId] ??
          ({
            contactId,
            items: [],
            firstUnreadId: null,
            loading: false,
            meta: null,
            error: null,
          } as ThreadMessages)

        const shouldReplace = forced || hasItems || prev.items.length === 0
        const nextItems = shouldReplace ? normalized : prev.items

        return {
          byContact: {
            ...current.byContact,
            [contactId]: {
              contactId,
              items: nextItems,
              firstUnreadId: response.first_unread_id ?? prev.firstUnreadId ?? null,
              loading: false,
              requestId,
              lastLoadedAt: Date.now(),
              meta: response.conversation ?? prev.meta ?? null,
              error: null,
            },
          },
        }
      })

      if (typeof window !== 'undefined' && window.__RPA_DEBUG?.net) {
        window.console.log('[messages] loadThread commit', {
          contactId,
          stationId,
          requestId,
          count: normalized.length,
          preserved: willPreserve,
          duration: Date.now() - startedAt,
        })
      }
    } catch (error) {
      if (controller.signal.aborted) {
        return
      }

      console.warn('[messages] loadThread failed', error)

      set((current) => {
        const prev =
          current.byContact[contactId] ??
          ({
            contactId,
            items: [],
            firstUnreadId: null,
            loading: false,
            meta: null,
            error: null,
          } as ThreadMessages)

        return {
          byContact: {
            ...current.byContact,
            [contactId]: {
              ...prev,
              contactId,
              loading: false,
              requestId,
              error: error instanceof Error ? error.message : 'Unable to load messages.',
            },
          },
        }
      })

      if (typeof window !== 'undefined' && window.__RPA_DEBUG?.net) {
        window.console.log('[messages] loadThread fail', {
          contactId,
          stationId,
          requestId,
          duration: Date.now() - startedAt,
        })
      }
    } finally {
      if (threadControllers.get(contactId) === controller) {
        threadControllers.delete(contactId)
      }
    }
  },
  setActiveContact: (contactId, stationId, options = {}) => {
    const normalizedContact = contactId ?? null
    const normalizedStation = stationId ?? null
    const state = get()
    const thread = normalizedContact ? state.byContact[normalizedContact] : undefined
    const hasCache = Boolean(thread && thread.items.length > 0)
    const lastLoadedAt = thread?.lastLoadedAt ?? 0
    const stale = !lastLoadedAt || Date.now() - lastLoadedAt > THREAD_STALE_MS
    const force = options.force ?? false
    const willFetch =
      Boolean(normalizedContact && normalizedStation) && (!!force || !hasCache || stale)

    if (typeof window !== 'undefined' && window.__RPA_DEBUG?.net) {
      window.console.log('[messages] setActiveContact', {
        contactId: normalizedContact,
        stationId: normalizedStation,
        hasCache,
        stale,
        force,
        willFetch,
      })
    }

    set({
      activeContactId: normalizedContact,
      activeStationId: normalizedStation,
    })

    if (!normalizedContact || !normalizedStation) {
      return
    }

    if (!hasCache || force) {
      void get().loadThread(normalizedContact, normalizedStation, { force })
      return
    }

    if (stale) {
      void get().loadThread(normalizedContact, normalizedStation)
    }
  },
  setJumpTarget: (contactId, messageId, options) => {
    set({
      jumpTarget: {
        contactId,
        messageId,
        highlight: options?.highlight ?? true,
        ts: Date.now(),
      },
    })
  },
  consumeJumpTarget: (contactId, messageId) => {
    set((state) => {
      const target = state.jumpTarget
      if (!target) {
        return state
      }

      if (target.contactId !== contactId || target.messageId !== messageId) {
        return state
      }

      return { ...state, jumpTarget: null }
    })
  },
  upsertThreadMessage: (contactId, message) => {
    const payload = { ...message, contact_id: contactId } as IncomingMessage
    get().appendBatch([payload])
  },
  removeThreadMessage: (contactId, id) => {
    set((state) => {
      const thread = state.byContact[contactId]
      const threadTarget = thread?.items.find((item) => item.id === id) ?? null
      const filteredItems = state.items.filter((item) => {
        if (item.id !== id) {
          return true
        }
        return false
      })

      const globalRemoved = filteredItems.length !== state.items.length
      let unreadDelta = 0

      if (globalRemoved) {
        const removedItem = state.items.find((item) => item.id === id)
        if (removedItem && !removedItem.is_read) {
          unreadDelta -= 1
        }
      }

      let nextByContact: MessagesByContact = state.byContact
      if (thread) {
        const nextItems = thread.items.filter((item) => item.id !== id)
        if (nextItems.length !== thread.items.length) {
          if (!globalRemoved && threadTarget && !threadTarget.is_read) {
            unreadDelta -= 1
          }

          const firstUnreadId = nextItems.find((item) => !item.is_read)?.id ?? null
          nextByContact = {
            ...state.byContact,
            [contactId]: {
              ...thread,
              items: nextItems,
              firstUnreadId,
            },
          }
        }
      }

      if (!globalRemoved && nextByContact === state.byContact) {
        return state
      }

      const unreadCount = Math.max(0, state.unreadCount + unreadDelta)

      return {
        items: filteredItems,
        unreadCount,
        byContact: nextByContact,
      }
    })
  },
  updateThreadMeta: (contactId, updater) => {
    set((state) => {
      const thread = state.byContact[contactId]
      if (!thread) {
        return state
      }

      const nextMeta = updater(thread.meta ?? null)
      if (nextMeta === thread.meta) {
        return state
      }

      return {
        byContact: {
          ...state.byContact,
          [contactId]: {
            ...thread,
            meta: nextMeta,
          },
        },
      }
    })
  },
  addOptimisticOutbound: (contactId, text, identityId, options = {}) => {
    const tempId = Date.now()

    set((state) => {
      const existingThread =
        state.byContact[contactId] ??
        ({
          contactId,
          items: [],
          firstUnreadId: null,
          loading: false,
          meta: null,
          error: null,
        } as ThreadMessages)

      const lastMessage = existingThread.items[existingThread.items.length - 1] ?? null
      const stationId = options.stationId ?? state.activeStationId ?? lastMessage?.station_id ?? 0
      const normalizedIdentityId = identityId ?? existingThread.meta?.active_identity ?? lastMessage?.contact_identity_id ?? 0
      const identityFromMeta = existingThread.meta?.identities.find((item) => item.id === normalizedIdentityId) ?? null
      const platform =
        options.platform ??
        lastMessage?.platform ??
        identityFromMeta?.platform ??
        existingThread.meta?.identities[0]?.platform ??
        'unknown'
      const contactName =
        options.contactName ??
        existingThread.meta?.contact.name ??
        lastMessage?.contact.name ??
        ''
      const contactLocation =
        options.contactLocation ??
        existingThread.meta?.contact.location ??
        lastMessage?.contact.location ??
        null
      const sentByUserId = options.sentByUserId ?? null
      const sentByDisplayName = options.sentByDisplayName ?? null

      const nowMs = Date.now()
      const optimisticIso = new Date(nowMs).toISOString()
      const optimisticEpochSeconds = Math.floor(nowMs / 1000)

      const optimistic: Message = {
        id: tempId,
        station_id: stationId,
        contact_id: contactId,
        contact_identity_id: normalizedIdentityId,
        platform,
        content: text,
        content_type: 'text',
        intent: null,
        received_at: optimisticIso,
        received_at_iso_utc: optimisticIso,
        received_at_epoch: optimisticEpochSeconds,
        is_read: true,
        is_flagged: false,
        is_on_air: false,
        is_autoresponder: false,
        round_id: null,
        reply_text: null,
        reply_at: null,
        reply_at_iso_utc: null,
        reply_at_epoch: null,
        direction: 'out',
        reply_to_message_id: options.replyToMessageId ?? null,
        sent_by_user_id: sentByUserId,
        transport_status: 'queued',
        transport_error: null,
        auto_rule_id: null,
        auto_rule_name: null,
        contact: {
          id: contactId,
          name: contactName,
          location: contactLocation,
        },
        identity: identityFromMeta
          ? {
              id: identityFromMeta.id,
              display_name: identityFromMeta.display_name,
              platform: identityFromMeta.platform,
            }
          : lastMessage?.identity ?? {
              id: normalizedIdentityId,
              display_name: contactName,
              platform,
            },
        sent_by:
          sentByUserId && sentByDisplayName
            ? {
                id: sentByUserId,
                display_name: sentByDisplayName,
              }
            : lastMessage?.sent_by ?? null,
      }

      const nextThreadItems = sortMessagesAscending([...existingThread.items.filter((item) => item.id !== tempId), optimistic])
      const firstUnreadId = nextThreadItems.find((item) => !item.is_read)?.id ?? null

      const filteredGlobal = state.items.filter((item) => item.id !== tempId)
      const nextGlobal = [optimistic, ...filteredGlobal].sort((a, b) => b.id - a.id)

      return {
        items: nextGlobal,
        byContact: {
          ...state.byContact,
          [contactId]: {
            ...existingThread,
            contactId,
            items: nextThreadItems,
            firstUnreadId,
          },
        },
      }
    })

    return tempId
  },
  finalizeOptimisticOutbound: (contactId, tempId, message) => {
    let resolved: Message | null = null

    set((state) => {
      const thread = state.byContact[contactId]
      if (!thread) {
        return state
      }

      const withoutTemp = thread.items.filter((item) => item.id !== tempId && item.id !== message.id)
      const nextThreadItems = sortMessagesAscending([...withoutTemp, message])
      const firstUnreadId = nextThreadItems.find((item) => !item.is_read)?.id ?? null

      const globalWithoutTemp = state.items.filter((item) => item.id !== tempId && item.id !== message.id)
      const nextGlobal = [message, ...globalWithoutTemp].sort((a, b) => b.id - a.id)

      resolved = message

      return {
        items: nextGlobal,
        byContact: {
          ...state.byContact,
          [contactId]: {
            ...thread,
            items: nextThreadItems,
            firstUnreadId,
          },
        },
      }
    })

    return resolved
  },
  failOptimisticOutbound: (contactId, tempId, error) => {
    set((state) => {
      const updatedItems = state.items.map((item) => {
        if (item.id !== tempId) {
          return item
        }

        return {
          ...item,
          transport_status: 'failed',
          transport_error: error,
        }
      })

      const thread = state.byContact[contactId]
      if (!thread) {
        return {
          items: updatedItems,
          byContact: state.byContact,
        }
      }

      const threadItems = thread.items.map((item) => {
        if (item.id !== tempId) {
          return item
        }

        return {
          ...item,
          transport_status: 'failed',
          transport_error: error,
        }
      })

      return {
        items: updatedItems,
        byContact: {
          ...state.byContact,
          [contactId]: {
            ...thread,
            items: threadItems,
          },
        },
      }
    })
  },
  markReadUpTo: (contactId, upToId) => {
    if (!contactId || !upToId) {
      return
    }

    set((state) => {
      let unreadDelta = 0
      const items = state.items.map((item) => {
        if (item.contact_id !== contactId || item.id > upToId || item.is_read) {
          return item
        }

        unreadDelta += 1
        return { ...item, is_read: true }
      })

      const thread = state.byContact[contactId]
      let nextByContact: MessagesByContact = state.byContact

      if (thread) {
        let threadChanged = false
        const threadItems = thread.items.map((message) => {
          if (message.contact_id !== contactId || message.id > upToId || message.is_read) {
            return message
          }

          threadChanged = true
          return { ...message, is_read: true }
        })

        if (threadChanged) {
          const firstUnreadId = threadItems.find((message) => !message.is_read)?.id ?? null
          nextByContact = {
            ...state.byContact,
            [contactId]: {
              ...thread,
              items: threadItems,
              firstUnreadId,
            },
          }
        }
      }

      if (unreadDelta === 0 && nextByContact === state.byContact) {
        return state
      }

      const unreadCount = Math.max(0, state.unreadCount - unreadDelta)

      return {
        items,
        unreadCount,
        byContact: nextByContact,
      }
    })
  },
  markConversationReadUpTo: (contactId, upToId) => {
    get().markReadUpTo(contactId, upToId)
  },
  markReadSingle: (contactId, messageId) => {
    if (!contactId || !messageId) {
      return
    }

    set((state) => {
      let unreadDelta = 0
      let globalChanged = false
      const items = state.items.map((item) => {
        if (item.contact_id !== contactId || item.id !== messageId || item.is_read) {
          return item
        }

        unreadDelta += 1
        globalChanged = true
        return { ...item, is_read: true }
      })

      const thread = state.byContact[contactId]
      let nextByContact: MessagesByContact = state.byContact

      if (thread) {
        let threadChanged = false
        const threadItems = thread.items.map((message) => {
          if (message.id !== messageId || message.is_read) {
            return message
          }

          threadChanged = true
          return { ...message, is_read: true }
        })

        if (threadChanged) {
          const firstUnreadId = threadItems.find((message) => !message.is_read)?.id ?? null
          nextByContact = {
            ...state.byContact,
            [contactId]: {
              ...thread,
              items: threadItems,
              firstUnreadId,
            },
          }
        }
      }

      if (!globalChanged && nextByContact === state.byContact) {
        return state
      }

      const unreadCount = globalChanged ? Math.max(0, state.unreadCount - unreadDelta) : state.unreadCount

      return {
        items: globalChanged ? items : state.items,
        unreadCount,
        byContact: nextByContact,
      }
    })
  },
  setConnected: (connected) => set({ connected }),
  setUnreadCount: (unreadCount) => set({ unreadCount }),
  setLastUpdateId: (id) => set({ lastUpdateId: id }),
  consumeIncoming: () => {
    let message: Message | null = null
    set((state) => {
      message = state.lastIncoming
      return { lastIncoming: null }
    })
    return message
  },
  setDefaultPlatforms: (platforms) => {
    const normalized = resolveDefaultPlatforms(platforms)
    set((state) => ({
      defaultPlatforms: normalized,
      filters: { ...state.filters, platforms: normalized },
    }))
  },
  resetFilters: () => {
    set((state) => ({
      filters: createInitialFilters(state.defaultPlatforms),
    }))
  },
  reset: () => {
    threadControllers.forEach((controller) => controller.abort())
    threadControllers.clear()

    set((state) => ({
      filters: createInitialFilters(state.defaultPlatforms),
      items: [],
      nextCursor: null,
      prevCursor: null,
      loading: false,
      unreadCount: 0,
      connected: false,
      lastId: 0,
      lastUpdateId: 0,
      lastIncoming: null,
      byContact: {},
      activeContactId: null,
      activeStationId: null,
    }))
  },
}))

if (typeof window !== 'undefined') {
  window.__RPA_DEBUG = {
    ...(window.__RPA_DEBUG ?? {}),
    store: {
      ...(window.__RPA_DEBUG?.store ?? {}),
      messages: useMessagesStore,
    },
  }
}

export const selectVisibleItems = (state: MessagesState): Message[] => {
  return state.items.filter((item) => matchesFilters(item, state.filters))
}

const sortStrings = (values: string[]): string[] => [...values].sort((a, b) => a.localeCompare(b))

export const isDefaultFilters = (filters: MessageFilters, defaultPlatforms: string[]): boolean => {
  if (filters.unreadOnly || filters.flaggedOnly || filters.onAirOnly) {
    return false
  }

  if (filters.intents.length > 0) {
    return false
  }

  if (filters.q.trim().length > 0) {
    return false
  }

  const normalizedDefaults = sortStrings(resolveDefaultPlatforms(defaultPlatforms))
  const normalizedPlatforms = sortStrings(dedupePlatforms(filters.platforms))

  if (normalizedDefaults.length !== normalizedPlatforms.length) {
    return false
  }

  return normalizedDefaults.every((value, index) => value === normalizedPlatforms[index])
}

export type { Message, ConversationMeta } from '../api/messages'

export default useMessagesStore

